// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
import BoxManager from '../viewer/BoxManager';
class SelectionManager implements Interfaces.Interfaces.ISelectionManager {

    private _selectedAssets: { [objectId: string]: string[]; } = {};
    private _selectedScans: { [objectId: string]: boolean; } = {};
    private _selectedAnnotations: { [objectId: string]: boolean; } = {};
    private _pendingSelectionAssets: { [objectId: string]: boolean; } = {};
    private _selectedAssemblies: { [objectId: string]: boolean; } = {};

    private _selectionHandler: Interfaces.Interfaces.IEventManager;
    private _highlightEngine: Interfaces.Interfaces.IHighlightEngine;
    private _meshManager: Interfaces.Interfaces.IMeshManager;
    private _materialManager: Interfaces.Interfaces.IMaterialsManager;
    private _annotationManager: Interfaces.Interfaces.IAnnotationManager;
    private _labelManager: Interfaces.Interfaces.ILabelManager;
    private _assemblyManager: Interfaces.Interfaces.IAssemblyManager;
    private _overrideManager: Interfaces.Interfaces.IMaterialOverrideManager;
    private _scene : BABYLON.Scene;
    private _numSelected: number = 0;
    private _multiSelectEnabled : boolean = false;

    // Selection override layer constants
    private static readonly SELECTION_LAYER_SOURCE = "selection";
    private static readonly SELECTION_LAYER_PRIORITY = .001; // must beat asset sets

    get selectionHandler() {
        return this._selectionHandler;
    }

    constructor(
        selectionHandler: Interfaces.Interfaces.IEventManager,
        meshManager: Interfaces.Interfaces.IMeshManager,
        highlightEngine: Interfaces.Interfaces.IHighlightEngine,
        materialManager: Interfaces.Interfaces.IMaterialsManager,
        annotationManager: Interfaces.Interfaces.IAnnotationManager,
        labelManager: Interfaces.Interfaces.ILabelManager,
        assemblyManager: Interfaces.Interfaces.IAssemblyManager,
        scene: any,
        overrideManager: Interfaces.Interfaces.IMaterialOverrideManager
    ) {
        this._selectionHandler = selectionHandler;
        this._highlightEngine = highlightEngine;
        this._meshManager = meshManager;
        this._materialManager = materialManager;
        this._annotationManager = annotationManager;
        this._labelManager = labelManager;
        this._assemblyManager = assemblyManager;
        this._overrideManager = overrideManager;
        this._scene = scene;
    }

    dispose() {
        this._withOverrideBatch(() => {
            for (const assetId in this._selectedAssets) {
                this._overrideManager.clearOverrideForSource(assetId, SelectionManager.SELECTION_LAYER_SOURCE);
            }
        });

        this._selectedAssets = {};
        this._pendingSelectionAssets = {};
        this._selectedScans = {};
        this._selectedAnnotations = {};
        this._selectedAssemblies = {};
        this._numSelected = 0;

        this._selectionHandler = null as any;
        this._highlightEngine = null as any;
        this._meshManager = null as any;
        this._materialManager = null as any;
        this._annotationManager = null as any;
        this._labelManager = null as any;
        this._assemblyManager = null as any;
        this._overrideManager = null as any;
    }

    setObjectsToMaintainSelection(objectIds: any[][]) {

        for (var x = 0; x < objectIds.length; x++) {

            var ids = objectIds[x];

            for (var y = 0; y < ids.length; y++) {

                if (this._selectedAssets[ids[y].objectId]) {

                    this._pendingSelectionAssets[ids[y].objectId] = true;
                }
            }
        }
    }

    maintainSelection(assetId: any, objectType: Enumerations.PickObjectType = Enumerations.PickObjectType.Unknown) {
        if (!this._pendingSelectionAssets[assetId]) {
            return;
        }

        if (this._tryMaintainPendingSelection(assetId, objectType)) {
            delete this._pendingSelectionAssets[assetId];
        }
    }

    getSelectedAssetIds(): string[] {
        const ids = new Set<string>();

        Object.keys(this._selectedAssets).forEach(id => ids.add(id));
        Object.keys(this._selectedScans).forEach(id => ids.add(id));
        Object.keys(this._selectedAnnotations).forEach(id => ids.add(id));
        Object.keys(this._selectedAssemblies).forEach(id => ids.add(id));

        return Array.from(ids);
    }

    getSelectedIdsWithType(): { id: string; objectType: Enumerations.PickObjectType }[] {
        const results: { id: string; objectType: Enumerations.PickObjectType }[] = [];

        Object.keys(this._selectedAssets).forEach(id => {
            results.push({ id: id, objectType: Enumerations.PickObjectType.Object });
        });

        Object.keys(this._selectedScans).forEach(id => {
            results.push({ id: id, objectType: Enumerations.PickObjectType.Scan });
        });

        Object.keys(this._selectedAnnotations).forEach(id => {
            results.push({ id: id, objectType: Enumerations.PickObjectType.Annotation });
        });

        Object.keys(this._selectedAssemblies).forEach(id => {
            results.push({ id: id, objectType: Enumerations.PickObjectType.Assembly });
        });

        return results;
    }

    selectionChanged(
        newSelection: string[],
        allSelectedIds: Interfaces.Interfaces.ISelectedObjectInfo[],
        isSelected: boolean,
        objectType: Enumerations.PickObjectType = Enumerations.PickObjectType.Unknown,
        ev?: PointerEvent
    ) {

        var args: Interfaces.Interfaces.ISelectionChangedEvent = {
            isSelected: isSelected,
            objectIds: newSelection,
            allSelectedObjects: allSelectedIds,
            objectSelectionType: objectType,
            ev: ev
        };

        this._selectionHandler.fireEvent(Enumerations.EventType.SELECTIONCHANGED, args);
    }

    selectSingleObject(assetId: string, unselectPrevious: boolean, fireEvents: boolean, objectType: Enumerations.PickObjectType = Enumerations.PickObjectType.Unknown, ev?: PointerEvent) {
        const resolvedObjectType = this._resolveSelectionObjectType(assetId, objectType);
        const isAlreadySelected = this._isAlreadySelected(assetId, resolvedObjectType);

        // Keep firing the selection event so click-specific behavior (such as a
        // context menu or Ctrl parent selection) still runs, but do not clear a
        // selection merely to reapply the same selected target.
        this.selectObjects([assetId], unselectPrevious && !isAlreadySelected, resolvedObjectType, fireEvents, true, ev);
        return true;
    }

    multiSelectEnabled() : boolean {
        return this._multiSelectEnabled;
    }

    setMultiSelectEnabled(enabled : boolean) {
        this._multiSelectEnabled = enabled;

        var args: Interfaces.Interfaces.ISelectionModeChangedEvent = {
            enabled : this._multiSelectEnabled
        };

        this._selectionHandler.fireEvent(Enumerations.EventType.SELECTIONMODECHANGED, args);
    }

    selectObjects(
        assets: string[],
        unselectPrevious: boolean,
        objectType: Enumerations.PickObjectType,
        fireEvents: boolean,
        fireBlockEvents: boolean = true,
        ev?: PointerEvent
    ) {

        var prevSelected = this.getSelectedIdsWithType();
        unselectPrevious = unselectPrevious && !this.multiSelectEnabled();


        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        if (unselectPrevious) {
            this.unselectObjects(Object.keys(this._selectedAssets), false, false, ev);
            this.unselectScans(Object.keys(this._selectedScans), false, false);
            this.unselectAnnotations(Object.keys(this._selectedAnnotations), false, false);
            this.unselectAssemblies(Object.keys(this._selectedAssemblies), false, false);

            this._materialManager.resetSelectionMaterial();
        }

        if (assets.length === 0) {
            if (fireBlockEvents) {
                this._selectionHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
            }
            return;
        }

        if (objectType == Enumerations.PickObjectType.Unknown && assets.length > 0) {
            objectType = this._resolveSelectionObjectType(assets[0], objectType);
        }

        if(this.multiSelectEnabled() && 
        (objectType == Enumerations.PickObjectType.GLBObject || 
        objectType == Enumerations.PickObjectType.Scan ||
        objectType  == Enumerations.PickObjectType.Assembly)
        ) {
            return;
        }

        this._labelManager.clearEditingGizmos();

        if (!fireEvents || objectType != Enumerations.PickObjectType.Object) {
        if (objectType == Enumerations.PickObjectType.Object
            || objectType == Enumerations.PickObjectType.Mesh
            || objectType == Enumerations.PickObjectType.PagedMesh
            || objectType == Enumerations.PickObjectType.GLBObject) {
            this._withOverrideBatch(() => {
                for (let i = 0; i < assets.length; i++) {
                    const assetId = assets[i];

                    if (this._isAlreadySelected(assetId, objectType)) {
                        continue;
                    }

                    if (this._pendingSelectionAssets[assetId]) {
                        delete this._selectedAssets[assetId];
                    }

                    if (!this._overrideManager.ensureBaseInfo(assetId)) {
                        this._pendingSelectionAssets[assetId] = true;
                        continue;
                    }

                    const baseMaterials = this._overrideManager.getBaseMaterials(assetId);
                    const masterMeshIds = this._overrideManager.getMasterMeshIds(assetId);

                    if (!baseMaterials || !masterMeshIds || baseMaterials.length === 0 || masterMeshIds.length === 0) {
                        this._pendingSelectionAssets[assetId] = true;
                        continue;
                    }

                    const isHidden = this._meshManager.isAssetHidden(assetId);
                    const selectionMaterials: string[] = [];

                    for (let z = 0; z < masterMeshIds.length; z++) {
                        const baseMatName = baseMaterials[0];

                        let selectionMaterialName: string;
                        if (isHidden) {
                            selectionMaterialName = this._materialManager.getHiddenSelectionMaterialName();
                        } else if (objectType == Enumerations.PickObjectType.Mesh
                            || objectType == Enumerations.PickObjectType.PagedMesh) {
                            selectionMaterialName = this._materialManager.getSelectionBackfaceMaterialName(baseMatName);
                        } else {
                            selectionMaterialName = this._materialManager.getSelectionMaterialName(baseMatName);
                        }

                        selectionMaterials.push(selectionMaterialName);
                    }

                    this._overrideManager.setOverrideForSource(
                        assetId,
                        SelectionManager.SELECTION_LAYER_SOURCE,
                        SelectionManager.SELECTION_LAYER_PRIORITY,
                        selectionMaterials
                    );

                    if (!this._selectedAssets[assetId]) {
                        this._selectedAssets[assetId] = [];
                    }

                    this._selectedAssets[assetId] = masterMeshIds.slice(0);
                    this._numSelected++;
                    delete this._pendingSelectionAssets[assetId];
                }
            });
        }
        else if (objectType == Enumerations.PickObjectType.Scan) {
            for (var i = 0; i < assets.length; i++) {
                const assetId = assets[i];
                if (this._isAlreadySelected(assetId, objectType)) {
                    continue;
                }

                this._selectedScans[assetId] = true;
                this._annotationManager.pickScan(assetId, true);
                this._numSelected++;
            }
        }
        else if (objectType == Enumerations.PickObjectType.Annotation) {
            for (var i = 0; i < assets.length; i++) {
                const assetId = assets[i];
                if (this._isAlreadySelected(assetId, objectType)) {
                    continue;
                }

                this._selectedAnnotations[assetId] = true;
                this._labelManager.selectAnnotation(assetId, true);
                this._numSelected++;
            }
        }
        else if (objectType == Enumerations.PickObjectType.Assembly) {
            for (var i = 0; i < assets.length; i++) {
                const assetId = assets[i];
                if (this._isAlreadySelected(assetId, objectType)) {
                    continue;
                }

                this._selectedAssemblies[assetId] = true;
                this._assemblyManager.selectAssembly(assetId);
                this._numSelected++;
            }
        }
        this._scene.render();
        }

        if (fireEvents) {
            this.selectionChanged(assets, prevSelected, true, objectType, ev);
        }

        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
        }
    }

    unselectAssets(assetIDs: string[], type: Enumerations.PickObjectType , fireEvents: boolean) {
        switch(type)
        {
            case Enumerations.PickObjectType.Annotation:
                {
                    this.unselectAnnotations(assetIDs, fireEvents);
                    break;
                }
            case Enumerations.PickObjectType.Object:
                {
                    this.unselectObjects(assetIDs, fireEvents);
                    break;
                }
        }
    }

    unselectObjects(assets: string[], fireEvents: boolean, fireBlockEvents: boolean = true, ev?: PointerEvent) {

        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        this._withOverrideBatch(() => {
            for (let i = 0; i < assets.length; i++) {
                const assetId = assets[i];

                if (this._selectedAssets[assetId]) {
                    this._overrideManager.clearOverrideForSource(assetId, SelectionManager.SELECTION_LAYER_SOURCE);
                    delete this._selectedAssets[assetId];
                    this._numSelected--;
                }

                delete this._pendingSelectionAssets[assetId];
            }
        });

        if (fireEvents && assets.length > 0) {
            this.selectionChanged(assets, this.getSelectedIdsWithType(), false, Enumerations.PickObjectType.Object, ev);
        }

        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
        }
    }

    unselectScans(scans: string[], fireEvents: boolean, fireBlockEvents: boolean = true) {

        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        for (var i = 0; i < scans.length; i++) {
            if (this._selectedScans[scans[i]]) {
                this._annotationManager.pickScan(scans[i], false);
                delete this._selectedScans[scans[i]];
                this._numSelected--;
            }
        }

        if (fireEvents && scans.length > 0) {
            this.selectionChanged(scans, this.getSelectedIdsWithType(), false, Enumerations.PickObjectType.Scan);
        }

        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
        }
    }

    unselectAnnotations(annotations: string[], fireEvents: boolean, fireBlockEvents: boolean = true) {

        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        for (var i = 0; i < annotations.length; i++) {
            if (this._selectedAnnotations[annotations[i]]) {
                this._labelManager.selectAnnotation(annotations[i], false);
                delete this._selectedAnnotations[annotations[i]];
                this._numSelected--;
            }
        }

        if (fireEvents && annotations.length > 0) {
            this.selectionChanged(annotations, this.getSelectedIdsWithType(), false, Enumerations.PickObjectType.Annotation);
        }

        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
        }
    }

    unselectAssemblies(assemblies: string[], fireEvents: boolean, fireBlockEvents: boolean = true) {

        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.STARTBLOCKINGOPERATION);
        }

        for (var i = 0; i < assemblies.length; i++) {
            if (this._selectedAssemblies[assemblies[i]]) {
                this._assemblyManager.selectAssembly(null);
                delete this._selectedAssemblies[assemblies[i]];
                this._numSelected--;
            }
        }

        if (fireEvents && assemblies.length > 0) {
            this.selectionChanged(assemblies, this.getSelectedIdsWithType(), false, Enumerations.PickObjectType.Assembly);
        }

        if (fireBlockEvents) {
            this._selectionHandler.fireEvent(Enumerations.EventType.ENDBLOCKINGOPERATION);
        }
    }

    unselectAllAssets(fireEvents: boolean) {
        // A hierarchy selection can remain pending while its geometry is
        // unloaded. Treat it as a real selection when clearing so the normal
        // selection-changed event also dismisses logical UI such as the
        // navigation breadcrumb.
        const selectedOrPendingObjectIds = Array.from(new Set([
            ...Object.keys(this._selectedAssets),
            ...Object.keys(this._pendingSelectionAssets),
        ]));
        this.unselectObjects(selectedOrPendingObjectIds, fireEvents);
        this.unselectScans(Object.keys(this._selectedScans), fireEvents);
        this.unselectAnnotations(Object.keys(this._selectedAnnotations), fireEvents);
        this.unselectAssemblies(Object.keys(this._selectedAssemblies), fireEvents);
        this._pendingSelectionAssets = {};
        this._scene.render();
    }

    private _withOverrideBatch(action: () => void): void {
        const om = this._overrideManager;
        const canBatch = om && typeof om.beginBatch === "function" && typeof om.endBatch === "function";
        if (!canBatch) {
            action();
            return;
        }

        om.beginBatch();
        try {
            action();
        } finally {
            om.endBatch();
        }
    }

    private _resolveSelectionObjectType(assetId: string, objectType: Enumerations.PickObjectType): Enumerations.PickObjectType {
        if (objectType !== Enumerations.PickObjectType.Unknown) {
            return objectType;
        }

        if (this._annotationManager.containsScan(assetId)) {
            return Enumerations.PickObjectType.Scan;
        }

        if (this._labelManager.containsAnnotation(assetId)) {
            return Enumerations.PickObjectType.Annotation;
        }

        if (this._assemblyManager.hasAssemblyInstance(assetId)) {
            return Enumerations.PickObjectType.Assembly;
        }

        return Enumerations.PickObjectType.Object;
    }

    private _tryMaintainPendingSelection(assetId: string, objectType: Enumerations.PickObjectType): boolean {
        const resolvedObjectType = this._resolveSelectionObjectType(assetId, objectType);

        if (resolvedObjectType == Enumerations.PickObjectType.Object
            || resolvedObjectType == Enumerations.PickObjectType.Mesh
            || resolvedObjectType == Enumerations.PickObjectType.PagedMesh
            || resolvedObjectType == Enumerations.PickObjectType.GLBObject) {
            const wasSelected = !!this._selectedAssets[assetId];

            if (!this._overrideManager.ensureBaseInfo(assetId)) {
                return false;
            }

            const baseMaterials = this._overrideManager.getBaseMaterials(assetId);
            const masterMeshIds = this._overrideManager.getMasterMeshIds(assetId);

            if (!baseMaterials || !masterMeshIds || baseMaterials.length === 0 || masterMeshIds.length === 0) {
                return false;
            }

            const isHidden = this._meshManager.isAssetHidden(assetId);
            const selectionMaterials: string[] = [];

            for (let index = 0; index < masterMeshIds.length; index++) {
                const baseMatName = baseMaterials[0];

                let selectionMaterialName: string;
                if (isHidden) {
                    selectionMaterialName = this._materialManager.getHiddenSelectionMaterialName();
                } else if (resolvedObjectType == Enumerations.PickObjectType.Mesh
                    || resolvedObjectType == Enumerations.PickObjectType.PagedMesh) {
                    selectionMaterialName = this._materialManager.getSelectionBackfaceMaterialName(baseMatName);
                } else {
                    selectionMaterialName = this._materialManager.getSelectionMaterialName(baseMatName);
                }

                selectionMaterials.push(selectionMaterialName);
            }

            this._overrideManager.setOverrideForSource(
                assetId,
                SelectionManager.SELECTION_LAYER_SOURCE,
                SelectionManager.SELECTION_LAYER_PRIORITY,
                selectionMaterials
            );

            this._selectedAssets[assetId] = masterMeshIds.slice(0);
            if (!wasSelected) {
                this._numSelected++;
            }

            return true;
        }
        if (resolvedObjectType == Enumerations.PickObjectType.Scan) {
            if (!this._selectedScans[assetId]) {
                this._selectedScans[assetId] = true;
                this._annotationManager.pickScan(assetId, true);
                this._numSelected++;
            }

            return true;
        }

        if (resolvedObjectType == Enumerations.PickObjectType.Annotation) {
            if (!this._selectedAnnotations[assetId]) {
                this._selectedAnnotations[assetId] = true;
                this._labelManager.selectAnnotation(assetId, true);
                this._numSelected++;
            }

            return true;
        }

        if (resolvedObjectType == Enumerations.PickObjectType.Assembly) {
            if (!this._selectedAssemblies[assetId]) {
                this._selectedAssemblies[assetId] = true;
                this._assemblyManager.selectAssembly(assetId);
                this._numSelected++;
            }

            return true;
        }

        return false;
    }

    private _isAlreadySelected(assetId: string, objectType: Enumerations.PickObjectType): boolean {
        switch (objectType) {
            case Enumerations.PickObjectType.Object:
            case Enumerations.PickObjectType.Mesh:
            case Enumerations.PickObjectType.PagedMesh:
            case Enumerations.PickObjectType.GLBObject:
                return !!(this._selectedAssets[assetId] && this._selectedAssets[assetId].length > 0) && !this._pendingSelectionAssets[assetId];

            case Enumerations.PickObjectType.Scan:
                return !!this._selectedScans[assetId];

            case Enumerations.PickObjectType.Annotation:
                return !!this._selectedAnnotations[assetId];

            case Enumerations.PickObjectType.Assembly:
                return !!this._selectedAssemblies[assetId];

            default:
                return false;
        }
    }
}

export default SelectionManager;
